import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { Router } from '@angular/router';

import { GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ShowInEpcotDirective } from '@frontend/sports/common/core/feature/show-in-epcot';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { SportsThemedButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { CouponTypes } from '@frontend/sports/coupons/feature/models';
import { CollapsedState, GridEvent, GridMedia } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { DsButton, DsButtonVariant } from '@frontend/ui/button';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { EventNameMapperService } from '../../event-details-common/event-name-mapper.service';
import { EventInfoComponent } from '../content/event-info.component';
import { EventMediaComponent } from '../content/event-media.component';

@Component({
    selector: 'ms-event-header',
    templateUrl: './event-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        SportsThemedButtonDirective,
        DsButton,
        IconCustomComponent,
        EventMediaComponent,
        EventInfoComponent,
        ShowInEpcotDirective,
    ],
    host: {
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'onMouseLeave()',
    },
})
export class EventHeaderComponent implements OnChanges {
    @Input() event: GridEvent;
    @Input() isOutrightMarket: boolean;
    @Input() isEventNameALink: boolean;
    @Input() eventUrl: string;
    @Input() media: GridMedia;
    @Input() isAllBetsButtonVisible: boolean;
    @Input() couponType: string;
    @Input() state?: CollapsedState;
    @Input() tracking: TrackingSource;
    @Input() isOptionsExist?: boolean;
    @Input() showScoreboardInfo: boolean;

    @Output() eventClick = new EventEmitter<Event>();
    @Output() headerDetails = new EventEmitter<{ trackDetails: string; expanded: boolean }>();
    @Output() getToggle = new EventEmitter<string>();

    isExpanded = true;
    couponHeader: boolean;
    isLeagueChildPageDesign: boolean;
    eventTitle: string;
    readonly buttonThemeVariant: ThemeOptions<DsButtonVariant> = { 'mgm-4': 'flat', 'mgm-dice': 'flat', 'sports-interaction': 'flat' };

    private readonly trackingCollapsed = 'Collapse';
    private readonly trackingExpanded = 'Expand';
    isEventHovered = signal(false);

    onMouseEnter() {
        this.isEventHovered.update(() => true);
    }

    onMouseLeave() {
        this.isEventHovered.update(() => false);
    }

    constructor(
        private eventNameMapper: EventNameMapperService,
        public sitecore: Sitecore,
        private router: Router,
        private gridConfig: GridConfig,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        this.couponHeader = this.couponType === CouponTypes.GoalScorer;
        this.isLeagueChildPageDesign = this.gridConfig.isLeagueChildPageDesign;
        if (changes.event) {
            this.eventTitle = this.getEventTitle();
        }
    }

    navigate(event: Event): void {
        this.eventClick.emit(event);
        event.preventDefault();
        event.stopPropagation();
        this.router.navigate([this.eventUrl]);
    }

    toggle(): void {
        this.isExpanded = !this.isExpanded;
        const eventDetails = this.isExpanded ? this.trackingExpanded : this.trackingCollapsed;
        this.headerDetails.emit({ trackDetails: eventDetails, expanded: this.isExpanded });
        this.getToggle.emit();
    }

    private getEventTitle(): string {
        return this.gridConfig.isShortEventInHeaderEnabled ? this.eventNameMapper.map(this.event, false) : this.event.name;
    }
}
